import React, { memo, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import ImageRP from "@/components/ImageRP";
import {
  CreativeMediaType,
  getCreativeMediaSourceType,
  getCreativeMediaType,
  getCreativeMediaUrl,
} from "@/features/campaigns/shared/helpers/creativePreviewHelper";

type CreativeMediaPreviewProps = {
  attachment?: string | null;
  channelName?: string | null;
  creativeName?: string | null;
  creativeType?: string | null;
  thumbnail?: string | null;
  poster?: string | null;
  className?: string;
  imageClassName?: string;
  imageStyle?: React.CSSProperties;
};

type CreativeMediaContentProps = CreativeMediaPreviewProps & {
  mediaType?: CreativeMediaType;
  controls?: boolean;
};

type CreativeMediaDialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string | null;
  channelName?: string | null;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  children: React.ReactNode;
};

const AUDIO_THUMBNAIL_ARTWORK = "/img/creatives/creative_audio.png";
const IMAGE_DEFAULT_ARTWORK = "/img/creatives/creative_image.png";

const AUDIO_PLAYER_ARTWORK = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="960" height="420" viewBox="0 0 960 420">
  <rect width="960" height="420" fill="#0f4a55"/>
  <g fill="#ffffff" transform="translate(360 132)">
    <rect x="0" y="68" width="10" height="54" rx="5"/>
    <rect x="21" y="43" width="10" height="104" rx="5"/>
    <rect x="42" y="25" width="10" height="140" rx="5"/>
    <rect x="63" y="8" width="10" height="174" rx="5"/>
    <rect x="84" y="32" width="10" height="126" rx="5"/>
    <rect x="105" y="51" width="10" height="88" rx="5"/>
    <rect x="126" y="20" width="10" height="150" rx="5"/>
    <rect x="147" y="44" width="10" height="102" rx="5"/>
    <rect x="168" y="0" width="10" height="190" rx="5"/>
    <rect x="189" y="18" width="10" height="154" rx="5"/>
    <rect x="210" y="35" width="10" height="120" rx="5"/>
    <rect x="231" y="62" width="10" height="66" rx="5"/>
    <rect x="252" y="76" width="10" height="38" rx="5"/>
  </g>
</svg>
`)}`;

const VIDEO_DEFAULT_ARTWORK = "/img/creatives/creative_video.png";

export function CreativeMediaDialog({
  open,
  onClose,
  title,
  channelName,
  maxWidth = "md",
  fullWidth = true,
  children,
}: CreativeMediaDialogProps) {
  const dialogTitleId = "creative-media-dialog-title";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={dialogTitleId}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle
        id={dialogTitleId}
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          minWidth={0}
          flexWrap="wrap"
        >
          {title ? (
            <Typography
              component="span"
              sx={{
                color: "var(--neutral-700)",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
          ) : null}
          {title && channelName ? (
            <Typography
              component="span"
              sx={{
                color: "var(--neutral-700)",
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              |
            </Typography>
          ) : null}
          {channelName ? (
            <Typography
              component="span"
              color="text.primary"
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {channelName}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pb: 2.5, pt: 0 }}>{children}</DialogContent>
    </Dialog>
  );
}

function getPreviewImage({
  mediaType,
  thumbnail,
  poster,
}: {
  mediaType: CreativeMediaType;
  thumbnail?: string | null;
  poster?: string | null;
}): string {
  if (thumbnail || poster) {
    return getCreativeMediaUrl(thumbnail || poster);
  }

  if (mediaType === "audio") {
    return AUDIO_THUMBNAIL_ARTWORK;
  }

  if (mediaType === "video") {
    return VIDEO_DEFAULT_ARTWORK;
  }

  return "";
}

export function CreativeMediaContent({
  attachment,
  channelName,
  creativeName,
  creativeType,
  thumbnail,
  poster,
  className = "",
  imageClassName = "img-fluid",
  imageStyle,
  mediaType,
  controls = false,
}: CreativeMediaContentProps) {
  const resolvedMediaType =
    mediaType ||
    getCreativeMediaType({ attachment, channelName, creativeType });
  const mediaUrl = getCreativeMediaUrl(attachment);
  const sourceType = getCreativeMediaSourceType(attachment);
  const previewImage = getPreviewImage({
    mediaType: resolvedMediaType,
    thumbnail,
    poster,
  });
  const audioPlayerArtwork =
    thumbnail || poster ? previewImage : AUDIO_PLAYER_ARTWORK;
  const altText = channelName || creativeName || "Creative";
  const thumbnailImageStyle: React.CSSProperties = {
    height: "auto",
    maxHeight: 320,
    maxWidth: "100%",
    objectFit: "contain",
    width: "auto",
    ...imageStyle,
  };

  if (resolvedMediaType === "audio") {
    if (controls) {
      return (
        <Box
          className={className}
          display="flex"
          flexDirection="column"
          width="100%"
          sx={{
            backgroundColor: "var(--primary-base-hover)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              minHeight: { xs: 220, sm: 320 },
              px: 2,
            }}
          >
            <img
              src={audioPlayerArtwork}
              alt={altText}
              className={`${imageClassName} creative-media-default-artwork`}
              style={{
                maxHeight: 320,
                objectFit: "contain",
                width: "100%",
                ...imageStyle,
              }}
            />
          </Box>
          <Box
            sx={{
              borderTop:
                "3px solid color-mix(in srgb, var(--neutral-white) 85%, transparent)",
              px: 1,
              py: 0.75,
            }}
          >
            <audio
              controls
              preload="metadata"
              data-testid="creative-audio-player"
              style={{ display: "block", width: "100%" }}
            >
              <source src={mediaUrl} type={sourceType || "audio/mpeg"} />
              Your browser does not support audio playback.
            </audio>
          </Box>
        </Box>
      );
    }

    return (
      <img
        src={previewImage}
        alt={altText}
        className={`${imageClassName} creative-media-default-artwork ${className}`.trim()}
        style={thumbnailImageStyle}
      />
    );
  }

  if (resolvedMediaType === "video") {
    if (controls) {
      return (
        <video
          controls
          preload="metadata"
          data-testid="creative-video-player"
          className={className || imageClassName}
          style={{ width: "100%", maxHeight: 520 }}
        >
          <source src={mediaUrl} type={sourceType} />
          Your browser does not support video playback.
        </video>
      );
    }

    return (
      <img
        src={previewImage}
        alt={altText}
        className={`${imageClassName} creative-media-default-artwork ${className}`.trim()}
        style={thumbnailImageStyle}
      />
    );
  }

  if (!attachment) {
    return (
      <img
        src={IMAGE_DEFAULT_ARTWORK}
        alt={altText}
        className={imageClassName}
        style={imageStyle}
      />
    );
  }

  return (
    <ImageRP
      src={attachment}
      type="CREATIVE"
      alt={altText}
      className={imageClassName}
      style={imageStyle}
    />
  );
}

function CreativeMediaPreview({
  attachment,
  channelName,
  creativeName,
  creativeType,
  thumbnail,
  poster,
  className = "",
  imageClassName = "img-fluid",
  imageStyle,
}: CreativeMediaPreviewProps) {
  const [open, setOpen] = useState(false);
  const mediaType = useMemo(
    () => getCreativeMediaType({ attachment, channelName, creativeType }),
    [attachment, channelName, creativeType]
  );
  const isPlayable = mediaType === "audio" || mediaType === "video";
  const previewLabel = `Preview ${mediaType}`;

  return (
    <>
      <Box
        className={className}
        position="relative"
        display="flex"
        justifyContent="center"
      >
        <CreativeMediaContent
          attachment={attachment}
          channelName={channelName}
          creativeName={creativeName}
          creativeType={creativeType}
          thumbnail={thumbnail}
          poster={poster}
          mediaType={mediaType}
          imageClassName={imageClassName}
          imageStyle={imageStyle}
        />
        {isPlayable ? (
          <IconButton
            aria-label={previewLabel}
            onClick={() => setOpen(true)}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor:
                "color-mix(in srgb, var(--neutral-white) 90%, transparent)",
              color: "var(--primary-base)",
              "&:hover": {
                backgroundColor: "var(--neutral-white)",
              },
            }}
          >
            <PlayArrowIcon />
          </IconButton>
        ) : null}
      </Box>
      {isPlayable ? (
        <CreativeMediaDialog
          open={open}
          onClose={() => setOpen(false)}
          title={creativeName || "Creative Preview"}
          channelName={channelName}
          maxWidth="md"
          fullWidth
        >
          <Box minWidth={240} display="flex" justifyContent="center">
            <CreativeMediaContent
              attachment={attachment}
              channelName={channelName}
              creativeName={creativeName}
              creativeType={creativeType}
              thumbnail={thumbnail}
              poster={poster}
              mediaType={mediaType}
              controls
              imageClassName="img-fluid"
            />
          </Box>
        </CreativeMediaDialog>
      ) : null}
    </>
  );
}

export default memo(CreativeMediaPreview);
